<template>
  <div class="slides" :class="{ 'low-desktop': isLowDesktop }">
    <h1>{{ questionsHeader }}</h1>
    <StepCounter class="step-counter--mobile" />
    <div class="question-blue-box">
      <Transition name="fade" mode="out-in">
        <div v-if="currentPage >= 0" class="question-container" :key="currentPage">
          <div>
            <StepCounter class="step-counter--desktop" />
            <div class="question-top">
              <h2 class="question-header">
                {{ getQuestionHeader }}
              </h2>
              <!-- Explainer modal (?) for each question -->
              <Modal
                v-if="
                  question.content.properties.explainerTitlePopup &&
                  question.content.properties.explainerTextPopup
                "
                :modal-open="explainerModalOpen"
                :has-info-button="true"
                :has-close-button="true"
                @close="handleExplainerModalClose"
                :title="question.content.properties.explainerTitlePopup"
                :description="question.content.properties.explainerTextPopup?.markup"
              />
            </div>
          </div>
          <div class="question-content" ref="questionContent">
            <DropDownPretty
              v-if="
                question.content.properties.questionType !== 'Custom' &&
                mapping[question.content.properties.questionType] === 'DropDown'
              "
              :options="getOptions(question)"
              :stateKey="question.content.properties.questionText"
            />
            <RadioChoicePretty
              v-if="
                question.content.properties.questionType !== 'Custom' &&
                mapping[question.content.properties.questionType] === 'Radio'
              "
              :options="getOptions(question)"
              :stateKey="question.content.properties.questionText"
            />
            <!-- TODO: set min and max from list values instead of Umbraco -->
            <NumberSliderPretty
              v-if="
                question.content.properties.questionType === 'Custom' &&
                question.content.properties.customType === 'Number'
              "
              :max="question.content.properties.numberRangeTo"
              :min="question.content.properties.numberRangeFrom"
              :maxLabel="question.content.properties.numberRangeToLabel"
              :minLabel="question.content.properties.numberRangeFromLabel"
              :stateKey="question.content.properties.questionText"
            />
          </div>
          <!-- <ScrollOverflowIndicator :spritemap="spritemap" /> -->
        </div>
      </Transition>
    </div>
    <div class="question-navigation">
      <button
        v-if="currentPage < questions.length - 1"
        class="button"
        @click="next"
        :disabled="question.content.properties.obligatory && !isCurrentQuestionValid"
      >
        {{ nextQuestionText }}
      </button>
      <button
        v-if="currentPage === questions.length - 1"
        class="button"
        @click="submit"
        :disabled="!isCurrentQuestionValid"
      >
        <Transition>
          <LoadingSpinner v-if="isSubmitting" />
          <span v-else>{{ submitText }}</span>
        </Transition>
      </button>
      <button v-if="currentPage > 0" class="button-back" @click="prev">
        <svg>
          <use :href="`${spritemap}#arrow-in-circle`"></use>
        </svg>
        <span> {{ d['Salary.Navigation.Back']?.item2 }}</span>
      </button>
    </div>
  </div>
  <!-- Member - Previous data notice popup -->
  <Modal
    v-show="showPreviousDataModal"
    :modal-open="previousDataModalOpen"
    :has-info-button="false"
    :has-close-button="false"
    @close="handlePreviousDataModalClose"
  >
    <template v-slot:default>
      <div class="modal-content">
        <span class="info-icon">i</span>
        <p>
          {{ d['Salary.PreviousDataNotice']?.item2 }}
        </p>
        <button
          class="button close-modal"
          @click="handlePreviousDataModalClose"
          :disabled="isSubmittingMasterData"
        >
          <button class="button close-modal" @click="handlePreviousDataModalClose">
            {{ d['Salary.Navigation.Continue']?.item2 }}
          </button>
        </button>
      </div>
    </template>
  </Modal>

  <!-- Member - Changed answers popup -->
  <Modal
    v-show="updateAnswersModalOpen"
    :modal-open="updateAnswersModalOpen"
    :title="d['Salary.UpdateAnswersModal.Title']?.item2"
    :has-info-button="false"
    :closes-on-overlay-click="false"
    @close="handleUpdateAnswersModalClose"
  >
    <template v-slot:default>
      <div class="changed-answers-modal-content">
        <div class="subheader underline">
          {{ d['Salary.UpdateAnswersModal.Subheader']?.item2 }}
        </div>
        <ul>
          <li v-for="(value, key) in changedAnswers" :key="key">
            <div class="question-category">{{ getQuestionCategory(key) }}</div>
            <strong class="changed-answer">{{ getListReadableAnswer(key, value) }}</strong>
            <div class="previous-answer">
              <span>{{ d['Salary.UpdateAnswersModal.PreviouslyRegistered']?.item2 }}</span>
              <span>
                {{ getListReadableAnswer(key, memberData[key]) }}
              </span>
            </div>
          </li>
        </ul>
        <div class="navigation">
          <button @click="abortUpdateAnswers" class="button-back">
            {{ d['Salary.UpdateAnswersModal.Abort']?.item2 }}
          </button>
          <button class="button" @click="submitWithChanges" :disabled="!isCurrentQuestionValid">
            <Transition>
              <LoadingSpinner v-if="isSubmittingMasterData" />
            </Transition>
            <span :class="{ loading: isSubmittingMasterData }">{{
              d['Salary.UpdateAnswersModal.Submit']?.item2
            }}</span>
          </button>
        </div>
      </div>
    </template>
  </Modal>

  <Modal
    v-show="showAdditionalWorkAreaModal"
    :modal-open="showAdditionalWorkAreaModal"
    :title="d['Salary.AdditionalWorkAreaModal.Title']?.item2"
    @close="showAdditionalWorkAreaModal = false"
  >
    <template v-slot>
      <div class="navigation">
        <button class="button-back" @click="handleAdditionalWorkAreaDecline">
          {{ d['Salary.AdditionalWorkAreaModal.ButtonDecline']?.item2 }}
        </button>
        <button class="button" @click="handleAdditionalWorkAreaAccept">
          {{ d['Salary.AdditionalWorkAreaModal.ButtonAccept']?.item2 }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script>
import { state, localDictionary } from '@/lib/state'
import { svgSpritemapSrc, getRelatedWorkFunctions } from '@/lib/mapping'
import DropDownPretty from './pretty/DropDownPretty.vue'
import NumberSliderPretty from './pretty/NumberSliderPretty.vue'
import RadioChoicePretty from './pretty/RadioChoicePretty.vue'
import StepCounter from './StepCounter.vue'
import Modal from '@/components/Modal.vue'
import ScrollOverflowIndicator from '@/components/ScrollOverflowIndicator.vue'
import { isLowDesktop } from '@/util/screenUtil'
import LoadingSpinner from '@/components/loading/LoadingSpinner.vue'
import { updateMasterDataForMember } from '@/services/user-data-service'

export default {
  data() {
    return {
      isMember: state.user.isMember,
      memberData: state.user.memberData,
      questions: state.questions,
      lists: state.lists,
      listKeys: state.listKeys, // Ensure listKeys is accessible in data
      mapping: state.questionTypeToRawComponent,
      d: localDictionary(),
      spritemap: svgSpritemapSrc,
      // Modal states
      explainerModalOpen: false,
      previousDataModalOpen: true,
      updateAnswersModalOpen: false,
      changedAnswers: {},
      showAdditionalWorkAreaModal: false,
      isAdditionalQuestionShown: false,
      isLowDesktop: isLowDesktop(),
    }
  },
  components: {
    DropDownPretty,
    NumberSliderPretty,
    RadioChoicePretty,
    StepCounter,
    Modal,
    ScrollOverflowIndicator,
    LoadingSpinner,
  },
  computed: {
    questionsHeader() {
      return state.questionsHeader || this.d['Salary.Questions.PageTitle']?.item2
    },
    currentPage() {
      return state.currentPage
    },
    question() {
      return state.questions[this.currentPage]
    },
    nextQuestionText() {
      const stateVal = state.answers[this.question.content.properties.questionText]
      const prefil = this.question.content.properties.prefilButtonText
      console.log(stateVal, prefil)
      if (!stateVal && prefil) {
        return prefil
      }
      return this.d['Salary.Navigation.Next']?.item2
    },
    submitText() {
      const stateVal = state.answers[this.question.content.properties.questionText]
      console.log('SUBMIT TEXT', this.question)
      const prefil = this.question.content.properties.prefilButtonText
      console.log(stateVal, prefil)
      if (!stateVal && prefil) {
        return prefil
      }
      return this.d['Salary.Navigation.Submit']?.item2
    },
    isCurrentQuestionValid() {
      const questionText = this.question.content.properties.questionText
      const currentAnswer = state.answers[questionText] || state.user?.memberData[questionText]
      const prefil = this.question.content.properties.prefilButtonText

      // If the current question is WorkFunction, require an answer
      if (questionText === 'Salary.Questions.WorkFunction') {
        return !!state.answers['Salary.Questions.WorkFunction']
      }

      // If the question is obligatory and no answer is provided, return false
      if (this.question.content.properties.obligatory && !currentAnswer && !prefil) {
        return false
      }

      return true
    },
    hasMemberData() {
      // Return true if any memberData fields are set (not null/undefined)
      console.log('Checking member data:', state.user.memberData)
      return Object.values(state.user.memberData).some((val) => val !== null && val !== undefined)
    },
    showPreviousDataModal() {
      return this.isMember && this.hasMemberData
    },
    // TODO: find a dynamic way to control which questions are shown
    isWorkFunctionQuestion() {
      return this.question.content.properties.questionText === 'Salary.Questions.WorkFunction'
    },
    isWorkAreaQuestion() {
      return this.question.content.properties.questionText === 'Salary.Questions.WorkArea'
    },
    getQuestionHeader() {
      if (this.isAdditionalQuestionShown) {
        if (this.isWorkFunctionQuestion) {
          return this.d['Salary.Questions.WorkFunctionSecondary']?.item2
        }
        if (this.isWorkAreaQuestion) {
          return this.d['Salary.Questions.WorkAreaSecondary']?.item2
        }
      }
      return this.d[this.question.content.properties.questionText]?.item2
    },
    isSubmitting() {
      return state.isSubmitting
    },
    isSubmittingMasterData() {
      return state.isSubmittingMasterData
    },
  },
  methods: {
    getQuestionCategory(key) {
      // Question category is always questionText + '.Category' in the dictionary
      return this.d[`${key}.Category`]?.item2 || 'Unknown Category'
    },
    getOptions(question) {
      let optionKey = this.listKeys[question.content.properties.questionText]

      // special case for work function that are nested under the workareas
      if (optionKey === 'position') {
        const selectedWorkAreaId = state.answers['Salary.Questions.WorkArea']
        return getRelatedWorkFunctions(selectedWorkAreaId)
      }

      return this.lists[optionKey] || []
    },
    getChangedAnswers() {
      const newAnswers = state.answers
      const previousAnswers = state.user.memberData
      console.log('OLD', state.answers)
      console.log('NEW', newAnswers)

      // Only compare HighestEducation, Leadership, WorkArea and WorkFunction as that is what we can update
      const changedAnswers = Object.keys(newAnswers).reduce((acc, key) => {
        if (
          key === 'Salary.Questions.HighestEducation' ||
          key === 'Salary.Questions.Leadership' ||
          key === 'Salary.Questions.WorkArea' ||
          key === 'Salary.Questions.WorkFunction'
        ) {
          if (newAnswers[key] !== previousAnswers[key]) {
            acc[key] = newAnswers[key]
          }
        }
        return acc
      }, {})

      return changedAnswers
    },
    getListReadableAnswer(key, value) {
      const listKey = this.listKeys[key]
      const list = this.lists[listKey]

      // WorkArea (uses workareaid and workareatext)
      if (key === 'Salary.Questions.WorkArea') {
        if (!list) return 'Unknown Work Area'
        const found = list.find((item) => item.workareaid === value)
        return found ? found.workareatext : 'Unknown Answer'
      }

      // WorkFunction (positions are nested in workareas)
      if (key === 'Salary.Questions.WorkFunction') {
        for (const workarea of this.lists['workareas'] || []) {
          if (Array.isArray(workarea.position)) {
            const pos = workarea.position.find((p) => p.id === value)
            if (pos) return pos.text
          }
        }
        return 'Unknown Work Function '
      }

      if (key === 'Salary.Questions.Leadership') {
        if (!list) return 'Unknown Answer'
        const found = list.find((item) => item.id === value)
        return found ? found.text : 'Unknown Answer'
      }

      if (key === 'Salary.Questions.HighestEducation') {
        if (!list) return 'Unknown Answer'
        const found = list.find((item) => item.id === value)
        return found ? found.text : 'Unknown Answer'
      }
    },
    next() {
      const stateVal = state.answers[this.question.content.properties.questionText]
      const prefil = this.question.content.properties.prefilButtonText

      if (!stateVal && prefil) {
        this.emitter.emit('run-updateValue', {
          target: {
            value: this.question.content.properties.numberRangeFrom.toString(),
          },
        })
      }

      //'run-updateValue'

      if (this.isWorkFunctionQuestion) {
        //Uncomment to show add additional workareas
        //this.showAdditionalWorkAreaModal = true

        const workArea = {
          categoryKey: state.answers['Salary.Questions.WorkArea'],
          subCategoryKey: state.answers['Salary.Questions.WorkFunction'],
        }
        state.selectedWorkAreas = [...state.selectedWorkAreas, workArea]
      }

      state.currentPage++

      this.emitter.emit('page-change')
    },
    prev() {
      state.currentPage--
      this.emitter.emit('page-change')
    },
    submit() {
      const stateVal = state.answers[this.question.content.properties.questionText]
      const prefil = this.question.content.properties.prefilButtonText

      if (!stateVal && prefil) {
        this.emitter.emit('run-updateValue', {
          target: {
            value: this.question.content.properties.numberRangeFrom.toString(),
          },
        })
      }
      if (this.isMember) {
        this.changedAnswers = this.getChangedAnswers()
        if (Object.keys(this.changedAnswers).length > 0) {
          // Open the update answers modal independently:
          this.updateAnswersModalOpen = true
        } else {
          this.emitter.emit('submit')
        }
      } else {
        this.emitter.emit('submit')
      }
    },
    handleAdditionalWorkAreaDecline() {
      const workArea = {
        categoryKey: state.answers['Salary.Questions.WorkArea'],
        subCategoryKey: state.answers['Salary.Questions.WorkFunction'],
      }
      state.selectedWorkAreas = [...state.selectedWorkAreas, workArea]

      this.showAdditionalWorkAreaModal = false
      this.isAdditionalQuestionShown = false
      state.currentPage++
      this.emitter.emit('page-change')
    },
    handleAdditionalWorkAreaAccept() {
      const workArea = {
        categoryKey: state.answers['Salary.Questions.WorkArea'],
        subCategoryKey: state.answers['Salary.Questions.WorkFunction'],
      }
      state.selectedWorkAreas = [...state.selectedWorkAreas, workArea]

      this.showAdditionalWorkAreaModal = false
      state.currentPage = state.questions.findIndex(
        (question) => question.content.properties.questionText === 'Salary.Questions.WorkArea',
      )
      this.isAdditionalQuestionShown = true
      this.emitter.emit('page-change')
    },
    async submitWithChanges() {
      state.isSubmittingMasterData = true

      try {
        console.log('SUBMITTING INSIDE submitWithChanges function on QuestionPage.Vue')
        const payload = {
          educationKey: state.answers['Salary.Questions.HighestEducation'],
          leadershipKey: state.answers['Salary.Questions.Leadership'],
          workAreas: state.selectedWorkAreas,
        }
        const response = await updateMasterDataForMember(payload)

        this.handleUpdateAnswersModalClose()
        this.emitter.emit('submit')
      } catch (error) {
        console.error('Error submitting data:', error)
      }
      state.isSubmittingMasterData = false
    },
    // Each modal has its own open/close methods:
    handleExplainerModalClose() {
      this.explainerModalOpen = false
    },
    handlePreviousDataModalClose() {
      this.previousDataModalOpen = false
    },
    handleUpdateAnswersModalClose() {
      this.updateAnswersModalOpen = false
    },
    abortUpdateAnswers() {
      this.updateAnswersModalOpen = false
      this.emitter.emit('submit')
    },
  },
}
</script>
<style lang="postcss" scoped>
h1 {
  display: none;
}

.question-container {
  position: relative;
  padding: 18px 8px 20px 8px;
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: opacity 2s ease-in-out;
  height: calc(100dvh - 264px);
}

.modal-content {
  display: flex;
  flex-direction: column;
  gap: 26px;
  align-items: center;
  text-align: center;

  p {
    font-size: var(--font-size-small);
    max-width: 460px;

    @media (min-width: 1024px) {
      font-size: var(--font-size-body);
    }
  }

  .info-icon {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    font-size: 24px;
    color: var(--color-blue);
    background-color: var(--color-green);
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-family: var(--font-it-ex);
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px; /* 200% */

    @media (min-width: 1024px) {
      font-size: 18px;
      width: 32px;
      height: 32px;
    }
  }
}

.continue-btn {
  align-self: center;
}

.question-blue-box {
  background-color: var(--color-blue);
  border-radius: 5px;
}

.explainer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: var(--color-white);

  .explainer-title {
    color: #fff;
    font-family: var(--font-it-ex);
    font-size: var(--font-size-small);
    line-height: 24px; /* 171.429% */
  }
}

.slides {
  position: relative;
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.question-top {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 10px;
  padding-bottom: 38px;
}

.question-header {
  text-transform: none;
  font-family: var(--font-it-ex);
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  margin-bottom: 0;
  color: var(--color-white);
}

.question-navigation {
  flex-direction: row-reverse;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-top: 10px;
}

.button-back {
  display: flex;
  align-items: center;
  gap: 14px;
  font-family: var(--font-it-ex);
  letter-spacing: 0.03em;
  font-size: var(--font-size-body);
  transition: all var(--ani-speed-2) var(--ani-type-1);

  span {
    transition: all var(--ani-speed-2) var(--ani-type-1);
  }

  svg {
    display: none;
  }
}

.button-back:hover,
.button-back:focus {
  span {
    margin-left: 4px;
  }
}

/* Fade Transition Styles */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.question-content {
  position: relative;
  height: calc(100dvh - 360px);
  overflow-y: auto;
  -ms-overflow-style: none;
  /* Internet Explorer 10+ */
  scrollbar-width: none;
  /* Firefox */
}
.question-content ::-webkit-scrollbar {
  display: none;
}

.content {
  width: 100%;
  height: 100%;
  overflow-y: scroll;
}

.changed-answers-modal-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-family: var(--font-it-ex);

  .title {
    font-size: var(--font-size-header-xsmall);
  }

  .question-category {
    font-size: var(--font-size-small);
  }

  .changed-answer {
    font-weight: 700;
    font-size: 15px;
  }

  .previous-answer {
    font-weight: 400;
    display: flex;
    gap: 1ch;
    font-size: var(--font-size-xs);

    &:first-child {
      flex-wrap: nowrap;
    }
  }

  .underline {
    border-bottom: solid 1px var(--color-black);
    margin-bottom: 4px;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}

.loading {
  opacity: 0;
}

.navigation {
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding-top: var(--grid-gutter-mobile);
}

@media (min-width: 1024px) {
  /* increase the size and space for .question-container */
  .slides.low-desktop {
    h1 {
      margin-bottom: var(--grid-gutter);
    }

    .question-top {
      padding-bottom: 60px;
    }

    .question-container {
      height: calc(100dvh - 323px);
      padding: 0px;
    }
  }

  .question-top {
    justify-content: flex-start;
    padding-bottom: var(--grid-gutter-2);
    padding-top: 12px;
  }

  .question-header {
    font-size: 24px;
  }

  h1 {
    display: block;
    color: var(--color-white);
    margin-bottom: var(--grid-gutter-3);
  }

  .question-container {
    max-width: 900px;
    background-color: transparent;
    height: calc(100dvh - 432px);
  }

  .question-content {
    height: 100%;
  }

  .question-navigation {
    color: var(--color-white);
    margin-top: 0;
  }

  .button-back {
    svg {
      width: 26px;
      height: 26px;
      display: block;
      transform: rotate(180deg);
    }
  }

  /* Scrollbar Styles */

  .question-content {
    /* Internet Explorer 10+ */
    scrollbar-width: auto;
    /* Firefox */
  }
  .question-content::-webkit-scrollbar {
    display: auto;
    width: 7px;
  }

  .question-content::-webkit-scrollbar-track {
    background: var(--color-darkblue);
    border-left: 2px solid transparent;
    border-right: 2px solid transparent;
    background-clip: padding-box;
  }

  .question-content::-webkit-scrollbar-thumb {
    background: var(--color-darkblue);
  }

  .question-content::-webkit-scrollbar-thumb {
    background-color: var(--color-darkblue);
    border-radius: 10px;
  }
}

.phone-input {
  display: flex;
  gap: 10px;

  #country-code {
    max-width: 4ch;
  }
}

.step-counter--mobile {
  @media (min-width: 1024px) {
    display: none;
  }
}

.step-counter--desktop {
  display: none;

  @media (min-width: 1024px) {
    display: block;
  }
}

/* Disable transition on desktop */
@media (min-width: 1024px) {
  .question-blue-box {
    background-color: transparent;
  }

  .button-back {
    height: 40px;
  }
}
</style>
